import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useEmailInbox, leadMessage, type EmailThread } from '../contexts/EmailInboxContext';
import { type ProxyEmailCategory, EMAIL_CATEGORY_LABEL } from '../utils/proxyEmailClassifier';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { buildProxyIntakeTasks, type ProxyTask } from '../data/proxyWorkflowRegistry';
import { SearchInput } from '../components/SearchInput';
import { SortableTh } from '../components/SortableTh';
import { useSort, sortRows } from '../utils/useSort';
import { EmailInboxDetailDrawer } from '../components/proxy/EmailInboxDetailDrawer';
import { pillStyle } from '../utils/pillStyle';
import { auditThread, AUDIT_TARGETS, type AuditTarget } from '../utils/proxyInboxAudit';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';

function CategoryChip({ category }: { category: ProxyEmailCategory | null | undefined }) {
  if (!category) {
    return <span className="inline-flex items-center text-xs font-medium px-2 py-[3px] rounded-md bg-wash text-muted border border-trim">Unclassified</span>;
  }
  if (category === 'RED_REPORT') {
    return <span className="inline-flex items-center text-xs font-medium px-2 py-[3px] rounded-md bg-[#F6FAFB] text-[#0F6E56]">Filed in Reports repository</span>;
  }
  return <span className="inline-flex items-center text-xs font-medium px-2 py-[3px] rounded-md bg-[#E9F6F6] text-[#063F5A]">{EMAIL_CATEGORY_LABEL[category]}</span>;
}

type Tab = 'proxy' | 'fulfillment';

// Team mailboxes copied on Proxy mail — a filter over the one shared inbox,
// not separate inboxes. verifications@ is where pulled EDGAR/SEDAR filings land.
const PROXY_MAILBOXES = ['documents@betanxt.com', 'verifications@betanxt.com'];

// ICT-2 audit view — today's proxymaterial@ mail that reached nobody who acts on it.
const AUDIT = 'audit';

function threadTouches(thread: EmailThread, mailbox: string): boolean {
  return thread.messages.some(m => m.to === mailbox || m.cc.includes(mailbox));
}

export function ProxyAllEmailsPage() {
  const { setTopbar } = useAppShell();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { threads, appendOutbound } = useEmailInbox();
  const workflowCtx = useProxyWorkflowContext();
  const [tab, setTab] = useState<Tab>(location.pathname.endsWith('/fulfillment') ? 'fulfillment' : 'proxy');
  const [search, setSearch] = useState('');
  const [mailbox, setMailbox] = useState<string | null>(() => searchParams.get('mailbox'));
  const [selected, setSelected] = useState<EmailThread | null>(null);
  const { sort, toggle } = useSort({ column: 'date', direction: 'desc' });
  const [auditOverride, setAuditOverride] = useState<Record<string, AuditTarget>>({});
  const [markedRead, setMarkedRead] = useState(false);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  useEffect(() => {
    setTopbar('Email Inbox', "Every email Ace has read or sent in the shared Proxy/Fulfillment mailbox, with what Ace classified, routed, or sent on your behalf.");
  }, [setTopbar]);

  // Deep-link from a linked task (Intake Queue / Escalations) back to its
  // source email — switches tab and scrolls to that thread's row, leaving
  // it to the user to click in and open it.
  const emailId = searchParams.get('emailId');
  const highlightThreadId = useMemo(
    () => (emailId ? threads.find(t => t.messages.some(m => m.id === emailId))?.id ?? null : null),
    [emailId, threads],
  );

  // `?mailbox=` picks a mailbox view (e.g. the ICT-2 audit) from a link.
  const mailboxParam = searchParams.get('mailbox');
  useEffect(() => { if (mailboxParam) setMailbox(mailboxParam); }, [mailboxParam]);

  // `&open=1` (the demo guide) also opens the drawer — once per arrival.
  const openOnArrival = searchParams.get('open') === '1';
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightThreadId) return;
    const thread = threads.find(t => t.id === highlightThreadId);
    if (thread) setTab(thread.origin);
    if (thread && openOnArrival && openedFor.current !== `${emailId}`) {
      openedFor.current = `${emailId}`;
      setSelected(thread);
    }
  }, [highlightThreadId, threads, openOnArrival, emailId]);

  useEffect(() => {
    if (!highlightThreadId) return;
    rowRefs.current.get(highlightThreadId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  const auditFindings = useMemo(() => {
    const map = new Map<string, ReturnType<typeof auditThread>>();
    threads.filter(t => t.origin === 'proxy').forEach(t => {
      const finding = auditThread(t.messages.map(m => ({ ...m, subject: t.subject })), PROXY_SEED_TODAY);
      if (finding) map.set(t.id, finding);
    });
    return map;
  }, [threads]);

  const rows = useMemo(() => threads.filter(t =>
    t.origin === tab && (tab !== 'proxy' || !mailbox
      || (mailbox === AUDIT ? auditFindings.has(t.id) : threadTouches(t, mailbox))),
  ), [threads, tab, mailbox, auditFindings]);

  function forwardAudited(thread: EmailThread, target: AuditTarget) {
    const dest = AUDIT_TARGETS[target];
    const original = leadMessage(thread);
    appendOutbound({
      refKey: thread.id, threadId: thread.id, jobIds: thread.jobNumbers, origin: 'proxy',
      to: dest.to, cc: dest.cc,
      subject: `Fwd: ${thread.subject}`,
      body: `Forwarded from the Proxy Material Inbox (ICT-2 audit). It didn't reach any ICT team member, documents@ or verifications@.\n\nFrom: ${original?.from} <${original?.fromEmail}>\n\n${original?.body ?? ''}`,
    });
  }

  const allTasks = useMemo(() => buildProxyIntakeTasks(workflowCtx), [workflowCtx]);
  const tasksByThread = useMemo(() => {
    const map = new Map<string, ProxyTask[]>();
    for (const th of threads) {
      const linked = allTasks.filter(t =>
        th.messages.some(m => t.sourceEmailId === m.id) || th.jobNumbers.includes(t.jobId),
      );
      if (linked.length) map.set(th.id, linked);
    }
    return map;
  }, [allTasks, threads]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(x =>
      x.subject.toLowerCase().includes(q) ||
      leadMessage(x)?.from.toLowerCase().includes(q) ||
      x.jobNumbers.some(j => j.includes(q)),
    );
  }, [rows, search]);

  const sorted = useMemo(() => sortRows(filtered, sort, (r, column) => {
    const first = leadMessage(r);
    if (column === 'date') return first?.date ?? '';
    if (column === 'from') return first?.from ?? '';
    if (column === 'subject') return r.subject;
    if (column === 'category') return r.category || '';
    return '';
  }), [filtered, sort]);

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          {tab === 'proxy' && (
            <div data-tour="inbox-mailboxes" className="flex gap-1.5 flex-wrap mr-1">
              <button onClick={() => setMailbox(null)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors" style={pillStyle(!mailbox)}>All mail</button>
              {PROXY_MAILBOXES.map(m => (
                <button key={m} data-tour={`mailbox-${m.split('@')[0]}`} onClick={() => setMailbox(mailbox === m ? null : m)}
                  className="px-3 py-1 rounded-full text-xs font-medium transition-colors" style={pillStyle(mailbox === m)}>
                  {m.split('@')[0]}@
                </button>
              ))}
              <button data-tour="mailbox-audit" onClick={() => setMailbox(mailbox === AUDIT ? null : AUDIT)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-colors" style={pillStyle(mailbox === AUDIT)}>
                Audit{auditFindings.size ? ` · ${auditFindings.size}` : ''}
              </button>
            </div>
          )}
          <span className="text-xs font-semibold whitespace-nowrap bg-wash border border-trim rounded-full px-3 py-1 text-copy">
            {rows.length} thread{rows.length === 1 ? '' : 's'}
          </span>
        </div>
        <SearchInput value={search} onChange={setSearch} placeholder="Search subject, sender, job #…" width={280} />
      </div>

      {tab === 'proxy' && mailbox === AUDIT && (
        <div data-tour="audit-banner" className="flex items-center justify-between gap-4 mb-4 px-4 py-3 rounded-xl border border-trim bg-wash">
          <div className="text-xs text-copy">
            <strong className="text-ink">ICT-2 audit · today's Proxy Material Inbox.</strong> These reached no ICT team member, documents@, verifications@ or notifications@.
            Ace picked where each one should go. Change it if needed, then forward. Never delete: the mailbox is the repository.
          </div>
          {markedRead
            ? <span className="text-xs text-[#0F6E56] font-medium whitespace-nowrap">Today's mail marked as read</span>
            : <button onClick={() => setMarkedRead(true)} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">Mark today's mail as read</button>}
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <SortableTh label="Date" column="date" sort={sort} onSort={toggle} />
            <SortableTh label="From" column="from" sort={sort} onSort={toggle} />
            <SortableTh label="Subject" column="subject" sort={sort} onSort={toggle} />
            {tab === 'proxy' && <SortableTh label="Category" column="category" sort={sort} onSort={toggle} />}
            <SortableTh label="Job" sort={sort} onSort={toggle} />
            <SortableTh label="Messages" sort={sort} onSort={toggle} />
            <SortableTh label={mailbox === AUDIT ? "Ace's call" : 'Related Task'} sort={sort} onSort={toggle} />
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => {
            const first = leadMessage(r);
            const relatedTasks = tasksByThread.get(r.id) ?? [];
            return (
              <tr
                key={r.id}
                data-tour={`email-row-${r.id}`}
                ref={el => { if (el) rowRefs.current.set(r.id, el); else rowRefs.current.delete(r.id); }}
                className={`border-b border-trim cursor-pointer hover:bg-wash transition-colors ${r.id === highlightThreadId ? 'bg-[#FFF7D6]' : ''}`}
                onClick={() => setSelected(r)}
              >
                <td className="px-2 py-2.5 text-xs text-muted whitespace-nowrap">{first?.date}</td>
                <td className="px-2 py-2.5 text-xs text-copy max-w-[220px] truncate" title={first?.sentByAce ? first.fromEmail : first?.from}>{first?.sentByAce ? first.fromEmail : first?.from}</td>
                <td className="px-2 py-2.5 text-sm text-ink max-w-[320px] truncate">{r.subject}</td>
                {tab === 'proxy' && <td data-tour="email-category" className="px-2 py-2.5 whitespace-nowrap"><CategoryChip category={r.category} /></td>}
                <td className="px-2 py-2.5 text-xs text-copy max-w-[160px] truncate" title={r.jobNumbers.join(', ') || undefined}>{r.jobNumbers.join(', ') || '-'}</td>
                <td className="px-2 py-2.5 text-xs text-copy whitespace-nowrap">{r.messages.length}</td>
                {mailbox === AUDIT && auditFindings.get(r.id) ? (() => {
                  const finding = auditFindings.get(r.id)!;
                  const target = auditOverride[r.id] ?? finding.target;
                  return (
                    <td data-tour="audit-call" className="px-2 py-2.5 text-xs" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <select
                          value={target}
                          onChange={e => setAuditOverride(prev => ({ ...prev, [r.id]: e.target.value as AuditTarget }))}
                          className="text-xs border border-trim rounded-md px-1.5 py-1 bg-white text-ink"
                        >
                          {(Object.keys(AUDIT_TARGETS) as AuditTarget[]).map(k => <option key={k} value={k}>{AUDIT_TARGETS[k].label}</option>)}
                        </select>
                        <button onClick={() => forwardAudited(r, target)} className="text-xs font-semibold text-[#2563EB] whitespace-nowrap">Forward</button>
                      </div>
                      <div className="text-muted mt-1 max-w-[320px]">{target === finding.target ? finding.why : 'Changed by you.'}</div>
                    </td>
                  );
                })() : (
                <td data-tour="email-related-task" className="px-2 py-2.5 text-xs">
                  {relatedTasks.length ? relatedTasks.map(t => (
                    <button
                      key={t.id}
                      onClick={e => { e.stopPropagation(); navigate(`/proxy?taskId=${t.id}`); }}
                      className="text-[#2563EB] hover:underline block truncate max-w-[200px] text-left"
                      title={t.title}
                    >
                      {t.title}
                    </button>
                  )) : <span className="text-muted">-</span>}
                </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {selected && <EmailInboxDetailDrawer key={selected.id} thread={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
